import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Grid,
  Divider,
  Paper,
  Container,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ErrorIcon from "@mui/icons-material/Error";
import api from "../../api/axios";

const VendorOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  const vendorUser = JSON.parse(localStorage.getItem("user"));
  // console.log("Vendor user:", vendorUser);
  useEffect(() => {
    if (!vendorUser?._id) {
      navigate("/vendor/login", { replace: true });
      return;
    }

    fetchVendorOrders();
  }, []);

  const fetchVendorOrders = async () => {
    try {
      const { data } = await api.get("/vendor/orders", {
        params: { vendorId: vendorUser._id },
      });

      setOrders(data);
    } catch (err) {
      console.error(
        err.response?.data?.message || "Failed to load orders"
      );
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ORDER_PLACED":
        return "info";
      case "SHIPPED":
        return "warning";
      case "DELIVERED":
        return "success";
      case "CANCELLED":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    if (status === "SHIPPED" || status === "DELIVERED") {
      return <LocalShippingIcon sx={{ fontSize: 16, mr: 0.5 }} />;
    }
    return null;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
          Your Orders
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Manage and track all orders assigned to you
        </Typography>
      </Box>

      {/* No Orders */}
      {orders.length === 0 && (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <ErrorIcon sx={{ fontSize: 48, color: "textSecondary", mb: 2 }} />
          <Typography variant="h6" color="textSecondary">
            No orders yet
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            New orders will appear here
          </Typography>
        </Paper>
      )}

      {/* Orders List */}
      <Stack spacing={3}>
        {orders.map((order) => (
          <Card
            key={order._id}
            sx={{
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              },
              transition: "all 0.3s ease",
            }}
          >
            {/* Order Header */}
            <Box
              sx={{
                p: 2,
                backgroundColor: "#f9f9f9",
                borderBottom: "1px solid #e0e0e0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">
                  Order ID
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    fontFamily: "monospace",
                    color: "#0066c0",
                  }}
                >
                  {order._id.slice(-12).toUpperCase()}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Order Date
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {new Date(order.createdAt).toLocaleDateString("en-IN", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </Typography>
              </Box>
            </Box>

            <CardContent sx={{ p: 2 }}>
              {/* Items Section */}
              <Stack spacing={2}>
                {order.items?.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      gap: 2,
                      pb: 2,
                      borderBottom: idx < order.items.length - 1 ? "1px solid #e0e0e0" : "none",
                    }}
                  >
                    {/* Product Image */}
                    <Box
                      sx={{
                        flexShrink: 0,
                        width: 100,
                        height: 100,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          No Image
                        </Typography>
                      )}
                    </Box>

                    {/* Product Details */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{
                          mb: 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.title}
                      </Typography>

                      <Grid container spacing={2} sx={{ mb: 1 }}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary" display="block">
                            Price per unit
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ color: "#b12704" }}
                          >
                            ₹{Number(item.price).toLocaleString("en-IN")}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary" display="block">
                            Quantity
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {item.qty} {item.qty > 1 ? "items" : "item"}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Typography
                        variant="caption"
                        sx={{
                          color: "#b12704",
                          fontWeight: 600,
                          display: "block",
                        }}
                      >
                        Subtotal: ₹{Number(item.price * item.qty).toLocaleString("en-IN")}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* Order Summary */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Delivery Address
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {order.address?.addressLine1}, {order.address?.addressLine2}
                    </Typography>
                    <Typography variant="body2">
                      {order.address?.city}, {order.address?.state} {order.address?.pincode}
                    </Typography>
                    <Typography variant="body2">{order.address?.country}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Order Total
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ color: "#b12704", mt: 0.5 }}
                    >
                      ₹{Number(order.total).toLocaleString("en-IN")}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                      Payment: {order.paymentMethod?.toUpperCase()}
                    </Typography>
                    <Chip
                      icon={getStatusIcon(order.status)}
                      label={order.tracking?.at(-1)?.label || "Processing"}
                      color={getStatusColor(order.status)}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Action Buttons */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ justifyContent: "flex-end" }}
              >
                {order.status === "ORDER_PLACED" && (
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#ff9900",
                      "&:hover": { backgroundColor: "#e68a00" },
                    }}
                    onClick={() => navigate(`/vendor/orders/${order._id}`)}
                  >
                    Review & Accept
                  </Button>
                )}

                <Button
                  variant="outlined"
                  onClick={() => navigate(`/vendor/orders/${order._id}`)}
                >
                  View Details
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Container>
  );
};

export default VendorOrders;
